/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { ISysQueGaugeFacade } from '../../../../../data-access/src/lib/domain/facades-models/sysque-gauge-facade-interface';
import { SysQueGaugeService } from '../../../../../utils/src/lib/sysque-backend/sysque-gauge-service';
import { LocalStorageService } from '../../../../../utils/src/lib/local-storage/local-storage.service';
import { GlobalConstants } from '../../constants/global-constants';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import { SystemStateManager } from '../../../../../utils/src/lib/system-state-manager.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
/**
 * Facade class for the SysQueGauge feature.
 * This class provides a simplified interface for interacting with the SysQueGaugeService and LocalStorageService.
 */
export class SysQueGaugeFacade implements ISysQueGaugeFacade {
  constructor(
    private gaugeService: SysQueGaugeService,
    private localStorageService: LocalStorageService,
    private stateMangerService: SystemStateManager
  ) {}  

  public getSelectedSystemName(): string {
    return this.localStorageService.getStorage(GlobalConstants.SELECTED_SYSTEM_NAME);
  } 

  public updateDockableState(state: SysqueElectricalState): void {
    this.stateMangerService.setDockedState(state);
  }  

  /**
   * Retrieves the SysQue connect fitting variables.
   *
   * @returns A promise that resolves to an array of strings representing the SysQue connect fitting variables.
   */
  public getSysQueConnectFittingVariables(): Promise<string[]> {
    return this.gaugeService.getSysQueConnectFittingVariables();
  }

  /**
   * Updates the local storage with the specified key-value pair.
   * @param key - The key for the storage entry.
   * @param value - The value to be stored.
   */
  public updateLocalStorage(key: string, value: any): void {
    this.localStorageService.updateDispatchLocalStorage(key, value);
  }  

  /**
   * Retrieves the current system state from the local storage.
   * @returns The current system state.
   */
  public getSystemState(): SysqueElectricalState {
    return this.localStorageService.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
  }

  /**
   * Rotates the SysQue connect with the specified angle.
   * @param angle - The angle to rotate the SysQue connect.
   */
  public rotateSysQueConnect(angle: string): Observable<any> {
    return this.gaugeService.rotateSysQueConnect(angle);
  }

  /**
   * Initializes the SysQue gauge.
   */
  public initializeSysQueGauge(): Observable<any> {
    return this.gaugeService.initializeSysQueGauge();
  }

  /**
   * Closes the window.
   */
  public closeWindow(): void {
    this.gaugeService.closeWindow();
  }

  /**
   * Retrieves the system name of the selected element.
   *
   * @returns A promise that resolves to a string representing the system name of the selected element.
   */
  public getSelectedElementSystemName(): Promise<string> {
    return this.gaugeService.getSelectedElementSystemName();
  }
}
